import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { BottomSideBorder } from '@/features/guardian/components/bottom-side-border';
import {
  GuardianKidCardAvatar,
  type GuardianKidCardAvatarSource,
} from '@/features/guardian/components/guardian-kid-card-avatar';
import { levelColor } from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';
import { memo, useCallback, useMemo, useState } from 'react';
import { Pressable, type LayoutChangeEvent } from 'react-native';

export type GuardianKidCardProps = {
  name: string;
  level: number;
  progress: number;
  avatarSource?: GuardianKidCardAvatarSource;
  orientation?: 'vertical' | 'horizontal';
  onPress?: () => void;
};

const BOTTOM_BORDER_EDGE_WIDTH = 2;
const BOTTOM_BORDER_TIP_WIDTH = 0;
const BOTTOM_BORDER_STEPS = 14;

function clampProgress(progress: number) {
  return Math.max(0, Math.min(1, progress));
}

export const GuardianKidCard = memo(
  ({
    name,
    level,
    progress,
    avatarSource,
    orientation = 'vertical',
    onPress,
  }: GuardianKidCardProps) => {
    const styles = useStyles();
    const { scale } = useScaling();

    const normalizedProgress = clampProgress(progress);
    const percent = Math.round(normalizedProgress * 100);
    const color = useMemo(() => levelColor(level), [level]);
    const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });

    const handleLayout = useCallback((event: LayoutChangeEvent) => {
      const { width: nextWidth, height: nextHeight } = event.nativeEvent.layout;
      setMeasuredSize(prev =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    }, []);

    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={name}
        disabled={!onPress}
        onPress={onPress}
        onLayout={handleLayout}
        style={({ pressed }) => [styles.card, pressed && onPress ? styles.cardPressed : null]}>
        <Stack
          direction={orientation === 'horizontal' ? 'row' : 'column'}
          align="center"
          gap={scale(12)}
          paddingX={scale({ phone: 12, tablet: 24 })}
          paddingY={scale(10)}>
          <GuardianKidCardAvatar
            color={color}
            progress={normalizedProgress}
            source={avatarSource ?? imgUser}
            hasAvatar={Boolean(avatarSource)}
          />

          <Stack
            flex={orientation === 'horizontal' ? 1 : undefined}
            align={orientation === 'horizontal' ? 'flex-start' : 'center'}
            gap={orientation === 'horizontal' ? scale(4) : 0}>
            <Text
              numberOfLines={1}
              variant="Font Guardian App/Body/Body 16 Semibold"
              style={[styles.name, orientation === 'horizontal' && styles.textLeft]}>
              {name}
            </Text>

            <Stack
              direction="row"
              align="center"
              justify={orientation === 'horizontal' ? 'flex-start' : 'center'}
              gap={scale(4)}>
              <Stack style={[styles.levelBadge, { backgroundColor: color }]}>
                <Text
                  numberOfLines={1}
                  fontSize={scale(8)}
                  lineHeight={scale(11.2)}
                  fontWeight={'500'}
                  fontFamily={fontFamilies.openSans}
                  style={styles.levelBadgeText}>
                  {STRINGS.guardian.home.levelBadge.replace('{level}', String(level))}
                </Text>
              </Stack>
              <Text
                numberOfLines={1}
                variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
                style={styles.percent}>
                {percent}%
              </Text>
            </Stack>
          </Stack>
        </Stack>

        <BottomSideBorder
          color={color}
          edgeWidth={BOTTOM_BORDER_EDGE_WIDTH}
          height={measuredSize.height}
          radius={scale(12)}
          steps={BOTTOM_BORDER_STEPS}
          tipWidth={BOTTOM_BORDER_TIP_WIDTH}
          width={measuredSize.width}
        />
      </Pressable>
    );
  },
);

const useStyles = createStyles(({ scale }) => ({
  card: {
    backgroundColor: colors['0'],
    width: '100%',
    borderRadius: scale(12),
  },
  cardPressed: {
    opacity: 0.72,
  },
  textLeft: { textAlign: 'left' },
  name: {
    color: guardianColors['10'],
    textAlign: 'center',
  },
  levelBadge: {
    paddingHorizontal: scale(8),
    paddingVertical: scale(4),
    borderRadius: scale(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  levelBadgeText: {
    color: colors['0'],
  },
  percent: {
    color: guardianColors['10'],
  },
}));

export default GuardianKidCard;
